import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  ViewChild,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import { SearchFieldComponent } from '../../shared/components/search-field/search-field.component';
import { PageEvent } from '@angular/material/paginator';
import { toObservable } from '@angular/core/rxjs-interop';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatCardModule } from '@angular/material/card';
import { TableComponent } from '../../shared/components/table/table.component';
import { MailboxService } from '../../core/services/mailbox.service';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { StatusLabelComponent } from '../../shared/components/status-label/status-label.component';
import { MatTableModule } from '@angular/material/table';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { BytesToSizePipe } from '../../shared/pipes/bytes-to-size-pipe';
import {
  Mailbox,
  MailboxStatus,
  User,
  WebsiteMailboxStatus,
} from '../../shared/models/mailbox.models';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Status } from '../../shared/models/status-label.model';
import { MatButtonModule } from '@angular/material/button';
import { ServersService } from '../../core/services/servers.service';
import { WebsitesService } from '../../core/services/websites.service';
import { Website } from '../../shared/models/website.models';
import { Server } from '../../shared/models/server.models';
import { ConfirmationDialogComponent } from '../../shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent } from '../../shared/components/snackbar/snackbar.component';
import { StatusModalService } from '../../core/services/status-modal.service';
import { AddMailboxDialogComponent } from '../website-overview/dialogs/add-mailbox-dialog/add-mailbox-dialog.component';
import { WebsocketService } from '../../core/services/websocket.service';
import { AddMailServiceDialogComponent } from './dialogs/add-mail-service-dialog/add-mail-service-dialog.component';

@Component({
  selector: 'app-email',
  imports: [
    MatTabsModule,
    SearchFieldComponent,
    FontAwesomeModule,
    MatCardModule,
    TableComponent,
    MatTableModule,
    AsyncPipe,
    StatusLabelComponent,
    MatSlideToggleModule,
    MatTooltipModule,
    MatButtonModule,
  ],
  providers: [BytesToSizePipe],
  templateUrl: './email.component.html',
  styleUrl: './email.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailComponent implements OnInit, OnDestroy {
  private mailboxService = inject(MailboxService);

  private serversService = inject(ServersService);

  private websitesService = inject(WebsitesService);

  private sizePipe = inject(BytesToSizePipe);

  private dialog = inject(MatDialog);

  private snackbar = inject(MatSnackBar);

  private statusModalService = inject(StatusModalService);

  private websocketService = inject(WebsocketService);

  @ViewChild('table', { static: false }) table!: TableComponent<Mailbox>;

  refresh$ = new BehaviorSubject<void>(undefined);

  destroyed$ = new Subject();

  filters = signal<{ page: number; domainSearch?: string }>({ page: 1 });

  filters$ = toObservable(this.filters);

  totalServices = signal(0);

  mailboxes$ = combineLatest([this.filters$, this.refresh$.pipe(startWith(undefined))]).pipe(
    switchMap(([filters]) => this.mailboxService.searchMailbox(filters.page, filters.domainSearch)),
    tap(([, count]) => this.totalServices.set(count)),
    switchMap(([mailboxes]) => {
      const websiteIds = [...new Set(mailboxes.map((mailbox) => mailbox.websiteId))];
      const servers = [...new Set(mailboxes.map((mailbox) => mailbox.server))];
      return forkJoin({
        mailboxes: of(mailboxes),
        websites: this.websitesService.getWebsitesByIds(websiteIds),
        servers: this.serversService.getServersByIds(servers),
      });
    }),

    map(({ mailboxes, websites, servers }) => {
      const websitesMap = new Map(websites.Websites.map((website) => [website.id, website]));
      const serversMap = new Map(servers.map((server) => [server._id, server]));
      return mailboxes.map((mailbox) => ({
        ...mailbox,
        website: websitesMap.get(mailbox.websiteId),
        serverInfo: serversMap.get(mailbox.server),
      }));
    }),

    shareReplay(1),
  );

  usersFilters = signal<{ query?: string; skip?: number; take?: number }>({ take: 10 });

  usersFilters$ = toObservable(this.usersFilters);

  totalUsers = signal(0);

  mailboxUsers$ = combineLatest([
    this.usersFilters$,
    this.refresh$.pipe(startWith(undefined)),
  ]).pipe(
    switchMap(([filters]) => this.mailboxService.getMailboxUsers(filters)),
    tap(({ totalCount }) => this.totalUsers.set(totalCount)),
    map(({ users }) => users),
    shareReplay(1),
  );

  expandedMailboxId$ = new BehaviorSubject<number | null>(null);

  expandedMailbox$ = this.expandedMailboxId$.pipe(
    filter(Boolean),
    distinctUntilChanged(),
    switchMap((id) => this.mailboxService.getWebsiteMailbox(id)),
    map(({ mailbox }) => mailbox),
    shareReplay(1),
  );

  servers$ = this.serversService.getAllServers().pipe(
    catchError(() => of([])),
    shareReplay(1),
  );

  mailboxesColumns = [
    {
      columnDef: 'mailService',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
    {
      columnDef: 'website',
      header: 'Website',
      cell: (element: Mailbox & { website?: Website }) => `${element.website?.domain || 'Unknown'}`,
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: () => '',
      useTemplate: true,
    },
    {
      columnDef: 'server',
      header: 'Server',
      cell: (element: Mailbox & { serverInfo?: Server }) =>
        `${element.serverInfo?.hostname || 'Unknown'}`,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  mailboxUsersColumns = [
    {
      columnDef: 'mailbox',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
    {
      columnDef: 'email',
      header: 'Email',
      cell: (element: User) => `${element.address}`,
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: () => '',
      useTemplate: true,
    },
    {
      columnDef: 'quota',
      header: 'Used Quota',
      cell: (element: User) =>
        element.blockUsed !== -1
          ? `${this.sizePipe.transform(element.blockUsed)} / ${this.sizePipe.transform(element.blockHard)}`
          : 'N / A',
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  mailboxIdToggle = signal<number | null>(null);

  mailboxUserIdToggle = signal<number | null>(null);

  websiteIdUpdating = signal<number | null>(null);

  ngOnInit(): void {
    this.websocketService
      .listen<{ title: string }>('notification')
      .pipe(
        takeUntil(this.destroyed$),
        filter(
          ({ title }) =>
            title === 'Mailbox Created Successfully!' ||
            title === 'Mailbox Deleted Successfully!' ||
            title === 'Mailbox User Created Successfully!',
        ),
      )
      .subscribe(() => {
        this.refresh$.next();
      });

    this.websocketService
      .listen<{ body: string }>('notification')
      .pipe(
        takeUntil(this.destroyed$),
        filter(
          ({ body }) =>
            body ===
              `Mailbox User for website with id: ${this.websiteIdUpdating()} created successfully.` ||
            body ===
              `Mailbox User for website with id: ${this.websiteIdUpdating()} has been updated successfully.` ||
            body ===
              `Mailbox User for website with id: ${this.websiteIdUpdating()} deleted successfully.`,
        ),
      )
      .subscribe(() => {
        this.websiteIdUpdating.set(null);
        this.refresh$.next();
      });

    this.websocketService
      .listen<{ body: string }>('notification')
      .pipe(
        takeUntil(this.destroyed$),
        filter(
          ({ body }) =>
            body === `Mailbox with id: ${this.mailboxIdToggle()}, enabled successfully` ||
            body === `Mailbox with id: ${this.mailboxIdToggle()}, disabled successfully`,
        ),
      )
      .subscribe(() => {
        this.refresh$.next();
        this.mailboxIdToggle.set(null);
      });

    this.websocketService
      .listen<{ title: string }>('notification')
      .pipe(
        takeUntil(this.destroyed$),
        filter(({ title }) => title === `Mailbox User Toggled Successfully!`),
      )
      .subscribe(() => {
        this.refresh$.next();
        this.mailboxUserIdToggle.set(null);
      });
  }

  showMailboxUsers(row: Mailbox): void {
    this.expandedMailboxId$.next(row.websiteId);
    this.table.toggle(row);
  }

  handlePageEvent(event: PageEvent) {
    this.filters.update((f) => ({ ...f, page: event.pageIndex + 1 }));
  }

  onSearch(text: string) {
    this.filters.update((f) => ({ ...f, domainSearch: text }));
  }

  handleUsersPageEvent(event: PageEvent) {
    this.usersFilters.update((f) => ({ ...f, skip: event.pageIndex * (f.take || 10) }));
  }

  onUsersSearch(text: string) {
    this.usersFilters.update((f) => ({ ...f, query: text }));
  }

  onServiceToggleChange(event: MatSlideToggleChange, id: number): void {
    event.source.toggle(); // To prevent visual toggle
    this.mailboxIdToggle.set(id);
    this.mailboxService.toggleMailbox(id).subscribe();
  }

  onUserToggleChange(event: MatSlideToggleChange, id: number): void {
    event.source.toggle(); // To prevent visual toggle
    this.mailboxUserIdToggle.set(id);
    this.mailboxService.toggleMailboxUser(id, event.checked).subscribe();
  }

  addMailbox(): void {
    this.servers$
      .pipe(
        take(1),
        switchMap((servers) =>
          this.dialog
            .open(AddMailServiceDialogComponent, {
              data: { servers },
            })
            .afterClosed(),
        ),
        filter(Boolean),
        switchMap((data) => this.mailboxService.createMailbox(data)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Mailbox service creation started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  deleteMailbox(mailbox: Mailbox & { website?: Website }): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete Mail Service',
          paragraphs: [
            `This action will permanently delete the Mail Service of ${mailbox.website?.domain} and disable all mailboxes.`,
            `Are you sure you want to delete the Mail Service of ${mailbox.website?.domain}?`,
          ],
          primaryButtonText: 'Delete Mail Service',
          type: 'delete',
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.mailboxService.deleteMailbox(mailbox.id)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Mail service deletion started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  addNewMailboxUser(): void {
    this.servers$
      .pipe(
        take(1),
        switchMap((servers) =>
          this.dialog
            .open(AddMailboxDialogComponent, {
              data: { servers },
            })
            .afterClosed(),
        ),
        filter(Boolean),
        switchMap((data) => this.mailboxService.createMailboxUser(data)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Mailbox adding started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  addMailboxUser(mailbox: Mailbox & { website?: Website }): void {
    this.dialog
      .open(AddMailboxDialogComponent, {
        data: {
          subTitle: mailbox.website?.domain,
          mailboxId: mailbox.id,
          systemUserId: mailbox.website?.details.unixUserId,
          domain: mailbox.website?.domain,
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((data) => this.mailboxService.createMailboxUser(data)),
      )

      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Mailbox adding started!`,
            },
          });
          this.websiteIdUpdating.set(mailbox.website?.id || null);
          // this.refresh$.next();
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  editMailboxUser(user: User, domain: string): void {
    this.dialog
      .open(AddMailboxDialogComponent, {
        data: {
          title: 'Mailbox settings',
          subTitle: domain,
          primaryButtonText: 'Save Changes',
          user,
          domain,
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((data) => this.mailboxService.updateMailboxUser(user.id, data)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Mailbox update started!`,
            },
          });
          this.refresh$.next();
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  deleteMailboxUser(user: User): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete Mailbox',
          paragraphs: [
            `This action will permanently delete mailbox ${user.address} and all of its contents. If a backup is available, the mailbox can be restored.`,
            `Are you sure you want to delete mailbox ${user.address}?`,
          ],
          primaryButtonText: 'Delete Mailbox',
          type: 'delete',
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.mailboxService.deleteMailboxUser(user.id)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Mailbox deletion started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  toStatus(key: MailboxStatus): Status {
    switch (key) {
      case MailboxStatus.ACTIVE:
      case MailboxStatus.CREATED:
        return Status.ACTIVE;
      case MailboxStatus.CREATING:
        return Status.IN_PROGRESS;
      case MailboxStatus.UPDATING:
        return Status.PENDING;
      case MailboxStatus.DISABLED:
        return Status.DISABLED;
      default:
        return Status.ERROR;
    }
  }

  fromUserStatus(key: WebsiteMailboxStatus): Status {
    switch (key) {
      case WebsiteMailboxStatus.ACTIVE:
      case WebsiteMailboxStatus.CREATED:
        return Status.ACTIVE;
      case WebsiteMailboxStatus.CREATING:
        return Status.IN_PROGRESS;
      case WebsiteMailboxStatus.UPDATING:
        return Status.PENDING;
      case WebsiteMailboxStatus.DISABLED:
        return Status.DISABLED;
      default:
        return Status.ERROR;
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next('');
    this.destroyed$.complete();
  }
}
